import {
  applyAction,
  createRun,
  defaultBalance,
  defaultSnapshot,
  finishBattle,
  scoreOf,
  seedFromString,
  startBattle,
  SimError,
  type Action,
  type Balance,
  type BattleSim,
  type EnemyArchId,
  type MapDef,
  type ModifierId,
  type ProfileSnapshot,
  type RunState,
  type RunSubmission,
  waveFor,
} from '@holdline/sim';

export type GameEvent = { kind: 'state' } | { kind: 'error'; code: string } | { kind: 'battle_tick' };

export interface WavePreviewEntry {
  arch: EnemyArchId;
  count: number;
}

/**
 * Thin controller between the deterministic sim and the renderer.
 * Owns the RunState, steps the battle at real time, and notifies the view.
 */
export class Game {
  state: RunState;
  battle: BattleSim | null = null;
  /** Battle playback speed. Purely presentational: the sim ticks are identical. */
  speed = 1;
  readonly balance: Balance = defaultBalance;
  private acc = 0;
  private listeners: ((e: GameEvent) => void)[] = [];

  /** What the current run was started with; the submission reports exactly this. */
  private runSnapshot: ProfileSnapshot;

  constructor(
    readonly map: MapDef,
    readonly seedLabel: string,
    /** Profile levels and gear the next run starts with; the server checks them against what the player owns. */
    public snapshot: ProfileSnapshot = defaultSnapshot(),
    /** Challenge modifiers; empty for a normal run. */
    readonly modifiers: readonly ModifierId[] = [],
  ) {
    this.runSnapshot = snapshot;
    this.state = createRun(map, seedFromString(seedLabel), this.balance, snapshot, modifiers);
  }

  on(fn: (e: GameEvent) => void): void {
    this.listeners.push(fn);
  }

  private emit(e: GameEvent): void {
    for (const l of this.listeners) l(e);
  }

  /** Apply a player action; invalid actions surface as an error event and change nothing. */
  act(action: Action): boolean {
    try {
      applyAction(this.state, action, this.map, this.balance);
      this.emit({ kind: 'state' });
      return true;
    } catch (err) {
      if (err instanceof SimError) {
        this.emit({ kind: 'error', code: err.code });
        return false;
      }
      throw err;
    }
  }

  fight(): void {
    if (this.state.phase !== 'placing') return;
    this.battle = startBattle(this.state, this.map, this.balance);
    this.acc = 0;
    this.emit({ kind: 'state' });
  }

  cycleSpeed(): void {
    this.speed = this.speed >= 3 ? 1 : this.speed + 1;
    this.emit({ kind: 'state' });
  }

  /** Call every frame with elapsed ms. Steps the sim at a fixed tick; returns true while battling. */
  update(deltaMs: number): boolean {
    const b = this.battle;
    if (!b) return false;
    this.acc += Math.min(deltaMs, 250) * this.speed;
    const tickMs = this.balance.tickMs;
    let stepped = false;
    // Cap catch-up so a background tab does not fast-forward the whole wave in one frame.
    let guard = 12;
    while (this.acc >= tickMs && !b.done && guard-- > 0) {
      b.step();
      this.acc -= tickMs;
      stepped = true;
    }
    if (stepped) this.emit({ kind: 'battle_tick' });
    if (b.done) {
      finishBattle(this.state, b, this.balance);
      this.battle = null;
      this.emit({ kind: 'state' });
      return false;
    }
    return true;
  }

  score(): number {
    return scoreOf(this.state);
  }

  /** Composition of the wave the player is about to fight. */
  wavePreview(): WavePreviewEntry[] {
    const wave = waveFor(this.map, this.state.wave);
    if (!wave) return [];
    const counts = new Map<EnemyArchId, number>();
    for (const g of wave.spawns) counts.set(g.arch, (counts.get(g.arch) ?? 0) + g.count);
    return [...counts.entries()].map(([arch, count]) => ({ arch, count }));
  }

  submission(): RunSubmission {
    return {
      mapId: this.map.id,
      seedLabel: this.seedLabel,
      actions: this.state.actions,
      claimedScore: this.score(),
      snapshot: this.runSnapshot,
    };
  }

  /** Same daily seed again: the leaderboard keeps the best attempt, so retrying is the point. */
  restart(): void {
    this.battle = null;
    this.speed = 1;
    this.runSnapshot = this.snapshot;
    this.state = createRun(this.map, seedFromString(this.seedLabel), this.balance, this.snapshot, this.modifiers);
    this.emit({ kind: 'state' });
  }
}
